// AvatarFeaturesToolUI — branded tool-UI for the agent's `suggestAvatarFeatures`
// tool call, rendered inline in the Aidea Assistant chat thread.
//
// The backend returns a JSON string (a card payload, like suggestImageStyles):
//   { "narration": "...the scene narration the avatar speaks...",
//     "persona": "formal safety presenter",
//     "category": "professional", "provider": "gemini-fast",
//     "features": [
//       { "key": "gender", "label": "Gender", "options": [
//         { "value": "female", "why": "neutral, approachable",
//           "fields": { "gender": "female" } }, ... ] }, ... ] }
//
// This card is a STEPPER: it walks the features one at a time. For each feature
// it shows the label, 3–4 option cards (each with `value` + `why`), and an
// "Other" free-text input. Back/Next move between features; a progress dots row
// shows position. After the last feature, an "Open avatar generator" button
// assembles the picks via assembleAvatarSettings and dispatches the existing
// `aidea:openAIPopup` avatar envelope (with category/provider merged in from the
// payload root). NO auto-open on mount — the user picks first, then clicks Open.
//
// Styling mirrors SuggestImageStylesToolUI: every utility is `tw-`-prefixed and
// takes effect under `.aidea-chat-root`. Brand tokens via CSS vars. No emoji.

import React, { useMemo, useState } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, AvatarIcon, AlertIcon } from "../icons";
import { dispatchOpenAIPopup } from "../aiPopupGateway";
import { assembleAvatarSettings } from "./assembleAvatarSettings";

// Parse the tool result defensively (string or object; never throw).
function parseFeaturesResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  const narration = typeof data.narration === "string" ? data.narration.trim() : "";
  const persona = typeof data.persona === "string" ? data.persona.trim() : "";
  const category = typeof data.category === "string" ? data.category.trim() : "";
  const provider = typeof data.provider === "string" ? data.provider.trim() : "";
  const features = Array.isArray(data.features)
    ? data.features.filter(
        (f) => f && typeof f.key === "string" && Array.isArray(f.options) && f.options.length
      )
    : [];
  return { narration, persona, category, provider, features };
}

// Small violet-tint header chip.
function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <AvatarIcon size={12} />
      {label}
    </span>
  );
}

// Card chrome shared by every state.
function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[520px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

// Running state: spinner + "Designing avatar options..."
function RunningState() {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Designing avatar options&hellip;
        </span>
      </div>
    </Card>
  );
}

// Generic error/empty card body.
function MessageCard({ children }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Avatar" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">{children}</span>
      </div>
    </Card>
  );
}

// Progress dots row: one dot per feature, the current one filled.
function ProgressDots({ total, current }) {
  return (
    <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-pb-2 tw-pt-1">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={[
            "tw-h-1.5 tw-rounded-full tw-transition-all",
            i === current
              ? "tw-w-4 tw-bg-[var(--aidea-primary)]"
              : i < current
                ? "tw-w-1.5 tw-bg-[var(--aidea-primary)] tw-opacity-50"
                : "tw-w-1.5 tw-bg-[var(--aidea-border)]",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

// A single clickable option card showing `value` + `why`.
function OptionCard({ option, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "tw-flex tw-flex-col tw-rounded-lg tw-border tw-px-2.5 tw-py-2 tw-text-left tw-transition-all tw-cursor-pointer",
        selected
          ? "tw-border-[var(--aidea-primary)] tw-ring-1 tw-ring-[var(--aidea-primary)] tw-bg-[var(--aidea-primary-tint)]"
          : "tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] hover:tw-border-[var(--aidea-primary)]",
      ].join(" ")}
    >
      <span className="tw-m-0 tw-text-[12.5px] tw-font-semibold tw-leading-snug tw-text-[var(--aidea-fg)]">
        {option.value}
      </span>
      {option.why && (
        <span className="tw-m-0 tw-mt-0.5 tw-text-[11px] tw-leading-snug tw-text-[var(--aidea-muted-fg)]">
          {option.why}
        </span>
      )}
    </button>
  );
}

// The interactive stepper. Renders one feature at a time; the final step shows
// the "Open avatar generator" button.
function Stepper({ narration, persona, category, provider, features }) {
  const [step, setStep] = useState(0);
  // picks: { [featureKey]: optionIndex | { custom: string } }
  const [picks, setPicks] = useState({});
  // Free-text drafts kept separately so the input stays controlled per feature.
  const [customText, setCustomText] = useState({});
  const [opened, setOpened] = useState(false);

  const total = features.length;
  const feature = features[step];
  const featureKey = feature.key;
  const currentPick = picks[featureKey];

  const selectOption = (index) => {
    setPicks((p) => ({ ...p, [featureKey]: index }));
  };

  const onCustomChange = (text) => {
    setCustomText((c) => ({ ...c, [featureKey]: text }));
    const trimmed = text.trim();
    setPicks((p) => {
      const next = { ...p };
      if (trimmed) next[featureKey] = { custom: text };
      else if (next[featureKey] && typeof next[featureKey] === "object") delete next[featureKey];
      return next;
    });
  };

  const isLast = step === total - 1;
  const customSelected = currentPick && typeof currentPick === "object";

  const handleOpen = () => {
    setOpened(true);
    const assembled = assembleAvatarSettings(features, picks);
    const settings = { ...assembled };
    // Merge category/provider from the payload root when not chosen by the user.
    if (!settings.category && category) settings.category = category;
    if (!settings.provider && provider) settings.provider = provider;
    dispatchOpenAIPopup({ feature: "avatar", prompt: narration, settings });
  };

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Avatar" />
        {persona && (
          <span className="tw-min-w-0 tw-truncate tw-text-[12px] tw-text-[var(--aidea-muted-fg)]">
            {persona}
          </span>
        )}
      </div>

      <ProgressDots total={total} current={step} />

      <div className="tw-px-3 tw-pb-1">
        <p className="tw-m-0 tw-text-[13px] tw-font-medium tw-text-[var(--aidea-fg)]">
          {feature.label || feature.key}
        </p>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-2 tw-px-3 tw-pt-1.5 sm:tw-grid-cols-2">
        {feature.options.slice(0, 4).map((option, index) => (
          <OptionCard
            key={index}
            option={option}
            selected={currentPick === index}
            onSelect={() => selectOption(index)}
          />
        ))}
      </div>

      {/* "Other" free-text fallback so the user is never boxed in. */}
      <div className="tw-px-3 tw-pt-2">
        <input
          type="text"
          value={customText[featureKey] || ""}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder="Other — type your own"
          className={[
            "tw-w-full tw-rounded-lg tw-border tw-px-2.5 tw-py-1.5 tw-text-[12.5px] tw-text-[var(--aidea-fg)] tw-outline-none placeholder:tw-text-[var(--aidea-muted-fg)]",
            customSelected
              ? "tw-border-[var(--aidea-primary)] tw-ring-1 tw-ring-[var(--aidea-primary)]"
              : "tw-border-[var(--aidea-border)] focus:tw-border-[var(--aidea-primary)]",
          ].join(" ")}
        />
      </div>

      {/* Footer: Back / Next or Open. */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-3">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="tw-rounded-lg tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-px-3 tw-py-1.5 tw-text-[12.5px] tw-font-medium tw-text-[var(--aidea-fg)] tw-transition-colors tw-cursor-pointer hover:tw-bg-[var(--aidea-muted-bg)] disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
        >
          Back
        </button>

        {isLast ? (
          <button
            type="button"
            onClick={handleOpen}
            className="tw-rounded-full tw-bg-[var(--ac-ink)] tw-px-3.5 tw-py-1.5 tw-text-[12.5px] tw-font-semibold tw-text-[var(--aidea-on-primary)] tw-transition-colors tw-cursor-pointer hover:tw-opacity-90"
          >
            {opened ? "Opening…" : "Open avatar generator"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
            className="tw-rounded-full tw-bg-[var(--ac-ink)] tw-px-3.5 tw-py-1.5 tw-text-[12.5px] tw-font-semibold tw-text-[var(--aidea-on-primary)] tw-transition-colors tw-cursor-pointer hover:tw-opacity-90"
          >
            Next
          </button>
        )}
      </div>
    </Card>
  );
}

export const AvatarFeaturesToolUI = makeAssistantToolUI({
  toolName: "suggestAvatarFeatures",
  render: ({ result, status }) => {
    if (status && status.type === "running") {
      return <RunningState />;
    }

    const parsed = parseFeaturesResult(result);
    if (!parsed) {
      return <MessageCard>Couldn&apos;t load avatar options.</MessageCard>;
    }
    if (parsed.error) {
      return <MessageCard>{parsed.error}</MessageCard>;
    }
    if (!parsed.features.length) {
      return (
        <MessageCard>
          No avatar options available — describe the presenter you want and I&apos;ll open the
          generator.
        </MessageCard>
      );
    }

    // `key` resets the stepper state if a new tool result mounts a fresh card.
    return (
      <Stepper
        key={parsed.features.length}
        narration={parsed.narration}
        persona={parsed.persona}
        category={parsed.category}
        provider={parsed.provider}
        features={parsed.features}
      />
    );
  },
});

export default AvatarFeaturesToolUI;
